import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// authDomain = el propio dominio del sitio en producción → OAuth handler same-origin,
// sin cookies third-party bloqueadas por Safari/Chrome iOS/incognito.
// Requiere que las URIs https://<host>/__/auth/handler estén autorizadas en
// Google Cloud Console → OAuth 2.0 Client (autorizado por r2vr01).
const productionHosts = ['funcomunion.es', 'www.funcomunion.es', 'funcomunion.web.app'];
const runtimeAuthDomain = (typeof window !== 'undefined' && productionHosts.includes(window.location.hostname))
  ? window.location.hostname
  : import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: runtimeAuthDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const firebaseAuth = getAuth(app);
export const firebaseStorage = getStorage(app);

// Firebase Cloud Messaging (push notifications)
let messaging: ReturnType<typeof getMessaging> | null = null;

export async function initMessaging() {
  if (messaging) return messaging;
  const supported = await isSupported();
  if (!supported) return null;
  messaging = getMessaging(app);
  return messaging;
}

// Internal: only runs if permission is already granted. Use for auto-setup on page load.
export async function requestPushToken(userId: string): Promise<string | null> {
  if (typeof window === 'undefined' || !('Notification' in window)) return null;
  // Do NOT prompt without a user gesture — browsers warn/block. Skip if not already granted.
  if (Notification.permission !== 'granted') return null;
  return doRegisterPushToken(userId);
}

// Call this from a click handler when the user explicitly opts in.
export async function promptAndRegisterPushToken(userId: string): Promise<string | null> {
  if (typeof window === 'undefined' || !('Notification' in window)) return null;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;
    return doRegisterPushToken(userId);
  } catch (err) {
    console.error('Push permission error:', err);
    return null;
  }
}

async function doRegisterPushToken(userId: string): Promise<string | null> {
  try {
    const msg = await initMessaging();
    if (!msg) return null;
    const sw = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const token = await getToken(msg, { serviceWorkerRegistration: sw });
    if (token) {
      // Adjuntar token Firebase del usuario para que el backend pueda validar ownership.
      (async () => {
        try {
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          try {
            const fbUser = (firebaseAuth as any)?.currentUser;
            if (fbUser) {
              const idTok = await fbUser.getIdToken();
              if (idTok) headers.Authorization = `Bearer ${idTok}`;
            }
          } catch (_) {}
          await fetch(`/api/users/${userId}/push-token`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ token }),
          });
        } catch (_) {}
      })();
    }
    return token;
  } catch (err) {
    console.error('Push token error:', err);
    return null;
  }
}

export function onPushMessage(callback: (payload: any) => void) {
  initMessaging().then(msg => {
    if (msg) onMessage(msg, callback);
  });
}
